import styled, { css } from 'styled-components'

import { Skeleton as Skeleton_ } from '@blade/ui-kit'

import type { ClassNameProps } from '@root/shared/react'

type TradingPairExploreItemSkeletonProps = {
  disabled?: boolean
} & ClassNameProps

export const TradingPairExploreItemSkeleton: React.FC<
  TradingPairExploreItemSkeletonProps
> = ({ disabled = false, className }) => {
  return (
    <Root aria-disabled={disabled} className={className}>
      <Side>
        <Icon>
          <SkeletonIcon
            height={80}
            width={80}
            variant="rectangle"
            css={css`
              border-radius: 12px;
            `}
          />
          <SkeletonIcon
            height={24}
            width={24}
            variant="circular"
            css={css`
              position: absolute;
              bottom: 2px;
              right: 3px;
              background: var(--background-4);
              border-radius: 50%;
            `}
          />
        </Icon>
      </Side>
      <Body>
        <CurrencyInformation>
          <Heading>
            <HeadingGroup>
              <Row
                css={css`
                  width: 55%;
                  grid-template-columns: 1fr 0.4fr;
                `}
              >
                <Skeleton height={15} width="100%" />
                <Skeleton height={15} width="100%" />
              </Row>
              <Row
                css={css`
                  width: 45%;
                  grid-template-columns: 2fr 1fr;
                `}
              >
                <Skeleton height={12} width="100%" />
                <Skeleton height={12} width="100%" />
              </Row>
            </HeadingGroup>
            <ButtonWrapper>
              <Skeleton height={28} width={66} />
            </ButtonWrapper>
          </Heading>
          <RowGroup>
            <Row
              css={css`
                width: 80%;
                grid-template-columns: 1fr 1fr 1fr 1fr;
              `}
            >
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="100%" />
            </Row>
            <Skeleton height={14} width={30} />
          </RowGroup>
          <RowGroup>
            <Row
              css={css`
                width: 80%;
                grid-template-columns: 1fr 1fr 1fr 1fr;
              `}
            >
              <Skeleton height={17} width="100%" />
              <Skeleton height={17} width="100%" />
              <Skeleton height={17} width="100%" />
            </Row>
            <Skeleton height={16} width={20} />
          </RowGroup>
        </CurrencyInformation>
      </Body>
    </Root>
  )
}

const Root = styled.div`
  grid-template-columns: 110px minmax(0, 1fr);

  all: unset;
  border: inset 1px solid var(--backgroundA-3);
  background: var(--background-2);

  height: 110px;
  border-radius: 12px;
  padding-inline: 12px;

  display: grid;
  grid-template-columns: auto minmax(0, 1fr);

  position: relative;
  overflow: hidden;

  content-visibility: auto;
  contain-intrinsic-size: auto 110px;
  contain: layout paint style;

  transition:
    background-color 0.1s linear,
    border-color 0.1s linear;

  border: 1px solid var(--backgroundA-2);

  &:hover {
    border: 1px solid var(--backgroundA-3);
    background: var(--background-2);
  }

  &[aria-disabled='true'] {
    border: 1px solid var(--backgroundA-1);

    &:hover {
      border: 1px solid var(--backgroundA-3);
      background: var(--background-2);
    }
  }
`

const Side = styled.div`
  display: grid;
  gap: 4px;

  align-items: center;
  justify-content: center;
  justify-items: center;
`

const Body = styled.div`
  display: grid;
  padding-block: 10px;
`

const Icon = styled.div`
  position: relative;
  padding: 2px;
  margin-right: 12px;
`

const Row = styled.div`
  display: grid;
  grid-auto-flow: column;

  gap: 6px;
`

const CurrencyInformation = styled.div`
  display: grid;

  grid-template-rows: auto auto;
  align-items: center;
  gap: 7px;

  align-content: center;
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
`

const HeadingGroup = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  gap: 7px;
`

const ButtonWrapper = styled.div`
  display: grid;
  justify-content: end;
`

const Skeleton = styled(Skeleton_)`
  background: var(--backgroundA-1);
  > div {
    border-radius: 2px;
  }

  ${Root}[aria-disabled='true'] & {
    > div:after {
      animation: none;
    }
  }
`

const SkeletonIcon = styled(Skeleton_)`
  background: var(--backgroundA-1);
  ${Root}[aria-disabled='true'] & {
    > div:after {
      animation: none;
    }
  }
`

const RowGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
`
